import React, { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { allocationService, studentService, requestService } from '../services/supabaseClient';
import '../styles/Registerperson.css';

// ============================================================
// DB CONNECTED
// ------------------------------------------------------------
// Course dropdown: only shows content actually ALLOCATED to this
// partner (allocationService.getForPartner), so the course_code and
// remaining seats are always accurate — no dummy list anymore.
//
// Registration:
//   1. Look up the student by email (studentService.findByEmail).
//      If they already exist, we REUSE that student_id — a person is
//      one row in `students` no matter how many courses they take.
//      If not, we create them.
//   2. Insert a row in `requests` linking student_id + course_id +
//      allocation_id + partner_id. This is what actually represents
//      "this person registered for this course" — so the SAME student
//      can have multiple request rows: several for the same course
//      (re-registration) or one per different course. Nothing here
//      blocks either case.
//   3. Bump allocation.seats_used by 1 (or by the batch size for bulk).
//
// The generated student_id comes straight back from Supabase after
// insert and is what's shown on the success screen / student list.
//
// BULK UPLOAD — CSV or Excel (.xlsx/.xls), and two course-assignment
// modes:
//   - 'same'  : one course picked in the dropdown, applied to every row
//               (file does not need a course_code column)
//   - 'file'  : every row carries its own `course_code` column, and is
//               registered against WHATEVER allocation that code
//               resolves to for this partner. Rows whose code doesn't
//               match one of this partner's allocations are flagged as
//               errors in the preview and block submission until fixed
//               (edit the file and re-upload).
//
// Because a bulk batch in 'file' mode can span several allocations,
// seats_used is bumped once per allocation actually used, by however
// many rows landed on it — not just once for the whole batch.
// ============================================================

const emptyForm = {
  courseId: '',
  person_first_name: '',
  person_last_name: '',
  person_email: '',
  person_mobile: '',
  person_organisation: ''
};

const REQUIRED_HEADERS = ['person_first_name', 'person_last_name', 'person_email', 'person_mobile'];

export default function RegisterPerson({ session, onViewStudents, onRegisterAnother }) {
  const [mode, setMode] = useState('single');
  const [allocations, setAllocations] = useState([]); // this partner's active allocations, joined with course
  const [loadingAllocations, setLoadingAllocations] = useState(true);

  const [form, setForm] = useState(emptyForm);

  // 'same' = one course applied to every row via the dropdown below.
  // 'file' = each row's own course_code column decides its allocation.
  const [courseAssignMode, setCourseAssignMode] = useState('same');

  const [bulkCourseId, setBulkCourseId] = useState('');
  const [bulkFile, setBulkFile] = useState(null);
  const [rawRows, setRawRows] = useState([]); // parsed rows, before course resolution

  const [error, setError] = useState('');
  const [fileError, setFileError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [singleSuccess, setSingleSuccess] = useState(null); // { studentId, name }
  const [bulkSuccess, setBulkSuccess] = useState(null); // { count, students: [{studentId, name}] }

  useEffect(() => {
    let cancelled = false;

    const loadAllocations = async () => {
      setLoadingAllocations(true);
      setError('');
      try {
        const data = await allocationService.getForPartner(session.partnerId);
        if (!cancelled) setAllocations(data || []);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Could not load your allocated content.');
      } finally {
        if (!cancelled) setLoadingAllocations(false);
      }
    };

    if (session?.partnerId) loadAllocations();
    return () => { cancelled = true; };
  }, [session?.partnerId]);

  const selectedSingleAllocation = allocations.find(a => a.course_id === form.courseId);
  const selectedBulkAllocation = allocations.find(a => a.course_id === bulkCourseId);

  const courseOptionLabel = (a) =>
    `${a.course?.name || 'Untitled'} · ${a.course?.type || '—'} — ${a.course?.course_code || a.course_id}`;

  // Map of course_code (lowercased) -> allocation, for 'file' mode lookups.
  const allocationByCourseCode = useMemo(() => {
    const map = {};
    allocations.forEach(a => {
      const code = a.course?.course_code;
      if (code) map[code.trim().toLowerCase()] = a;
    });
    return map;
  }, [allocations]);

  // Resolves each raw row to its allocation (+ an error string if it
  // can't be resolved). Recomputes whenever the mode, the dropdown
  // pick, the file rows, or the allocations list changes.
  const processedRows = useMemo(() => {
    return rawRows.map(row => {
      if (courseAssignMode === 'same') {
        return {
          ...row,
          allocation: selectedBulkAllocation || null,
          rowError: selectedBulkAllocation ? '' : 'No course selected above'
        };
      }

      const code = (row.course_code || '').trim();
      if (!code) {
        return { ...row, allocation: null, rowError: 'Missing course_code' };
      }

      const allocation = allocationByCourseCode[code.toLowerCase()];
      return {
        ...row,
        allocation: allocation || null,
        rowError: allocation ? '' : `Unknown or unallocated course_code "${code}"`
      };
    });
  }, [rawRows, courseAssignMode, selectedBulkAllocation, allocationByCourseCode]);

  const hasRowErrors = processedRows.some(row => row.rowError);

  // Finds (or creates) the student row for this email, then inserts one
  // `requests` row for the given allocation. Returns the resulting
  // student row. Seat bumping is handled by the caller (single row vs
  // per-allocation batch bump differ).
  const registerOneStudent = async ({ firstName, lastName, email, mobile, organisation, allocation }) => {
    let student = await studentService.findByEmail(email);

    if (!student) {
      student = await studentService.add({
        first_name: firstName,
        last_name: lastName,
        email,
        mobile: mobile || null,
        organisation: organisation || null
      });
    }

    await requestService.add({
      student_id: student.student_id,
      course_id: allocation.course_id,
      allocation_id: allocation.allocation_id,
      partner_id: session.partnerId,
      payment_status: 'pending',
      status: 'registered'
    });

    return student;
  };

  /* ============================================================
     SINGLE REGISTRATION
     ============================================================ */

  const handleSingleSubmit = async event => {
    event.preventDefault();
    setError('');

    if (!selectedSingleAllocation) {
      setError('Please select allocated content.');
      return;
    }

    if (!session?.partnerId) {
      setError('Partner session not found.');
      return;
    }

    setSubmitting(true);

    try {
      const student = await registerOneStudent({
        firstName: form.person_first_name,
        lastName: form.person_last_name,
        email: form.person_email,
        mobile: form.person_mobile,
        organisation: form.person_organisation,
        allocation: selectedSingleAllocation
      });

      await allocationService.adjustSeatsUsed(selectedSingleAllocation.allocation_id, 1);

      const fullName = `${student.first_name} ${student.last_name}`.trim();
      setSingleSuccess({ studentId: student.student_id, name: fullName });
      setForm(emptyForm);
    } catch (err) {
      setError(err.message || 'Unable to submit registration.');
    } finally {
      setSubmitting(false);
    }
  };

  /* ============================================================
     FILE PARSING (CSV or Excel) -> shared row shape
     ============================================================ */

  // Splits raw file bytes into an array-of-arrays (like a spreadsheet
  // grid: row 0 = headers, rest = data), whether the source was a CSV
  // or an Excel workbook. Everything downstream works off this same
  // grid shape so CSV and Excel share one code path.
  const readCsvAsGrid = file =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = e => {
        try {
          const text = e.target.result;
          const lines = text
            .split(/\r?\n/)
            .map(line => line.trim())
            .filter(Boolean);

          resolve(lines.map(line => line.split(',').map(cell => cell.trim())));
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error('Unable to read the CSV file.'));
      reader.readAsText(file);
    });

  const readExcelAsGrid = file =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = e => {
        try {
          const workbook = XLSX.read(e.target.result, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[firstSheetName];

          // header: 1 -> array-of-arrays instead of array-of-objects,
          // so it lines up with the CSV grid shape below.
          const grid = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', blankrows: false });

          resolve(
            grid.map(row => row.map(cell => String(cell ?? '').trim()))
          );
        } catch (err) {
          reject(new Error('Unable to read the Excel file.'));
        }
      };
      reader.onerror = () => reject(new Error('Unable to read the Excel file.'));
      reader.readAsArrayBuffer(file);
    });

  // Turns a header+data grid into the row objects the rest of the
  // form works with. `course_code` is picked up if present, but only
  // required (validated) when courseAssignMode === 'file'.
  const gridToRows = grid => {
    if (grid.length < 2) {
      throw new Error('File must contain a header row and at least one record.');
    }

    const headers = grid[0].map(h => h.toLowerCase());

    const missingHeaders = REQUIRED_HEADERS.filter(header => !headers.includes(header));
    if (missingHeaders.length > 0) {
      throw new Error(`Missing columns: ${missingHeaders.join(', ')}`);
    }

    if (courseAssignMode === 'file' && !headers.includes('course_code')) {
      throw new Error('Missing column: course_code (required when assigning course per row).');
    }

    const rows = grid
      .slice(1)
      .map((values, index) => {
        const row = {};
        headers.forEach((header, i) => {
          row[header] = (values[i] ?? '').toString().trim();
        });

        const firstName = row.person_first_name || '';
        const lastName = row.person_last_name || '';

        return {
          id: index + 1,
          person_first_name: firstName,
          person_last_name: lastName,
          person_name: `${firstName} ${lastName}`.trim(),
          person_email: row.person_email || '',
          person_mobile: row.person_mobile || '',
          person_organisation: row.person_organisation || '',
          course_code: row.course_code || ''
        };
      })
      .filter(row => row.person_name || row.person_email || row.person_mobile);

    if (!rows.length) {
      throw new Error('No valid participant records found.');
    }

    return rows;
  };

  const handleFileChange = async event => {
    const file = event.target.files?.[0];

    setFileError('');
    setError('');
    setBulkFile(null);
    setRawRows([]);

    if (!file) return;

    const lowerName = file.name.toLowerCase();
    const isCsv = lowerName.endsWith('.csv');
    const isExcel = lowerName.endsWith('.xlsx') || lowerName.endsWith('.xls');

    if (!isCsv && !isExcel) {
      setFileError('Please upload a CSV or Excel (.xlsx/.xls) file.');
      return;
    }

    setBulkFile(file);

    try {
      const grid = isCsv ? await readCsvAsGrid(file) : await readExcelAsGrid(file);
      const rows = gridToRows(grid);
      setRawRows(rows);
    } catch (err) {
      setFileError(err.message || 'Unable to read the file.');
      setBulkFile(null);
      setRawRows([]);
    }
  };

  /* ============================================================
     BULK REGISTRATION
     ============================================================ */

  const handleBulkSubmit = async event => {
    event.preventDefault();

    setError('');
    setFileError('');

    if (!bulkFile) {
      setFileError('Please select a CSV or Excel file.');
      return;
    }

    if (!processedRows.length) {
      setFileError('No valid records found in the file.');
      return;
    }

    if (courseAssignMode === 'same' && !selectedBulkAllocation) {
      setError('Please select a course or certification.');
      return;
    }

    if (hasRowErrors) {
      setError('Some rows have an unresolved course_code. Fix the file and re-upload.');
      return;
    }

    if (!session?.partnerId) {
      setError('Partner session not found.');
      return;
    }

    setSubmitting(true);

    try {
      const registered = [];
      const seatsByAllocationId = {}; // allocation_id -> count of rows registered against it

      // Sequential on purpose — findByEmail/add per row needs to see
      // each other so two identical rows in the same file don't create
      // two separate student records.
      for (const row of processedRows) {
        const student = await registerOneStudent({
          firstName: row.person_first_name,
          lastName: row.person_last_name,
          email: row.person_email,
          mobile: row.person_mobile,
          organisation: row.person_organisation,
          allocation: row.allocation
        });

        registered.push({
          studentId: student.student_id,
          name: `${student.first_name} ${student.last_name}`.trim()
        });

        const allocId = row.allocation.allocation_id;
        seatsByAllocationId[allocId] = (seatsByAllocationId[allocId] || 0) + 1;
      }

      // Bump seats_used once per allocation actually used, by however
      // many rows landed on it (matters in 'file' mode, where a single
      // upload can span several allocations).
      await Promise.all(
        Object.entries(seatsByAllocationId).map(([allocationId, count]) =>
          allocationService.adjustSeatsUsed(allocationId, count)
        )
      );

      setBulkSuccess({ count: registered.length, students: registered });

      setBulkFile(null);
      setRawRows([]);
      setBulkCourseId('');

      const input = document.getElementById('bulk-upload');
      if (input) input.value = '';
    } catch (err) {
      setError(err.message || 'Unable to process bulk registration.');
    } finally {
      setSubmitting(false);
    }
  };

  /* ============================================================
     CLEAR / RESET
     ============================================================ */

  const clearSingleForm = () => {
    setForm(emptyForm);
    setError('');
  };

  const clearBulk = () => {
    setBulkCourseId('');
    setBulkFile(null);
    setRawRows([]);
    setFileError('');
    setError('');

    const input = document.getElementById('bulk-upload');
    if (input) input.value = '';
  };

  // Switching between 'same' and 'file' invalidates any already-parsed
  // rows (course_code requirement changes), so clear the uploaded file
  // rather than leave stale/mismatched data around.
  const handleAssignModeChange = nextMode => {
    setCourseAssignMode(nextMode);
    setBulkFile(null);
    setRawRows([]);
    setFileError('');
    setError('');

    const input = document.getElementById('bulk-upload');
    if (input) input.value = '';
  };

  const registerAnotherSingle = () => {
    setSingleSuccess(null);
    onRegisterAnother?.();
  };

  const registerAnotherBulk = () => {
    setBulkSuccess(null);
    onRegisterAnother?.();
  };

  const downloadTemplate = () => {
    const csvContent =
      courseAssignMode === 'file'
        ? 'person_first_name,person_last_name,person_email,person_mobile,person_organisation,course_code\n' +
          'John,Doe,john@example.com,9876543210,Acme Inc,WZ-C-0001'
        : 'person_first_name,person_last_name,person_email,person_mobile,person_organisation\n' +
          'John,Doe,john@example.com,9876543210,Acme Inc';

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = 'registration_template.csv';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  /* ============================================================
     SINGLE SUCCESS SCREEN
     ============================================================ */

  if (singleSuccess) {
    return (
      <div className="panel narrow">
        <div className="success-screen">
          <div className="success-check">✓</div>
          <h2>Registration Successful!</h2>
          <p>{singleSuccess.name} has been registered successfully.</p>

          <div className="student-id-box">
            <span className="student-id-label">Student ID</span>
            <span className="student-id-value">{singleSuccess.studentId}</span>
          </div>

          <div className="success-actions">
            <button className="secondary" type="button" onClick={registerAnotherSingle}>
              Register Another
            </button>
            <button className="primary" type="button" onClick={onViewStudents}>
              Go to Student List
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ============================================================
     BULK SUCCESS SCREEN
     ============================================================ */

  if (bulkSuccess) {
    return (
      <div className="panel narrow">
        <div className="success-screen">
          <div className="success-check">✓</div>
          <h2>Registration Successful!</h2>
          <p>{bulkSuccess.count} participants have been registered successfully.</p>

          <div className="table-wrapper bulk-success-table">
            <table>
              <thead>
                <tr>
                  <th>Student ID</th>
                  <th>Name</th>
                </tr>
              </thead>
              <tbody>
                {bulkSuccess.students.slice(0, 10).map(s => (
                  <tr key={s.studentId}>
                    <td>{s.studentId}</td>
                    <td>{s.name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {bulkSuccess.students.length > 10 && (
              <div className="preview-more">Showing first 10 of {bulkSuccess.students.length}.</div>
            )}
          </div>

          <div className="success-actions">
            <button className="secondary" type="button" onClick={registerAnotherBulk}>
              Upload Another File
            </button>
            <button className="primary" type="button" onClick={onViewStudents}>
              Go to Student List
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ============================================================
     UI
     ============================================================ */

  return (
    <div className="panel narrow">
      <div className="panel-head">
        <div>
          <h2>Registration Form </h2>
          <p>Register Students against your allocated course or certification.</p>
        </div>
      </div>

      <div className="registration-tabs">
        <button
          type="button"
          className={mode === 'single' ? 'active' : ''}
          onClick={() => {
            setMode('single');
            setError('');
          }}
        >
          Single Registration
        </button>

        <button
          type="button"
          className={mode === 'bulk' ? 'active' : ''}
          onClick={() => {
            setMode('bulk');
            setError('');
          }}
        >
          Bulk CSV / Excel Upload
        </button>
      </div>

      {!loadingAllocations && !allocations.length && (
        <div className="error">
          No content has been allocated to you yet. Contact Wiiz Admin to get a course or
          certification allocated before registering students.
        </div>
      )}

      {/* SINGLE */}
      {mode === 'single' && (
        <form className="form-grid" onSubmit={handleSingleSubmit}>
          <label>
            Course / Certification
            <select
              required
              value={form.courseId}
              onChange={event => setForm({ ...form, courseId: event.target.value })}
              disabled={loadingAllocations}
            >
              <option value="">
                {loadingAllocations ? 'Loading allocated content…' : 'Select allocated Course & Certification'}
              </option>
              {allocations.map(a => (
                <option key={a.allocation_id} value={a.course_id}>
                  {courseOptionLabel(a)}
                </option>
              ))}
            </select>
          </label>

          <label>
            First Name
            <input
              required
              type="text"
              placeholder="Enter first name"
              value={form.person_first_name}
              onChange={event => setForm({ ...form, person_first_name: event.target.value })}
            />
          </label>

          <label>
            Last Name
            <input
              required
              type="text"
              placeholder="Enter last name"
              value={form.person_last_name}
              onChange={event => setForm({ ...form, person_last_name: event.target.value })}
            />
          </label>

          <label>
            Email
            <input
              required
              type="email"
              placeholder="Enter email address"
              value={form.person_email}
              onChange={event => setForm({ ...form, person_email: event.target.value })}
            />
          </label>

          <label>
            Mobile
            <input
              type="tel"
              placeholder="Enter mobile number"
              value={form.person_mobile}
              onChange={event => setForm({ ...form, person_mobile: event.target.value })}
            />
          </label>

          <label>
            Organisation
            <input
              type="text"
              placeholder="Enter organisation name"
              value={form.person_organisation}
              onChange={event => setForm({ ...form, person_organisation: event.target.value })}
            />
          </label>

          {error && <div className="error">{error}</div>}

          <div className="form-actions">
            <button className="secondary" type="button" onClick={clearSingleForm}>
              Clear
            </button>

            <button className="primary" type="submit" disabled={submitting}>
              {submitting ? 'Submitting...' : 'Submit Registration'}
            </button>
          </div>
        </form>
      )}

      {/* BULK */}
      {mode === 'bulk' && (
        <form className="bulk-upload-section" onSubmit={handleBulkSubmit}>
          <div className="bulk-content-selector">
            <label>
              How should each row's course be decided?
              <select
                value={courseAssignMode}
                onChange={event => handleAssignModeChange(event.target.value)}
              >
                <option value="same">Same course for everyone (pick below)</option>
                <option value="file">From file — each row has its own course_code</option>
              </select>
            </label>
          </div>

          {courseAssignMode === 'same' && (
            <div className="bulk-content-selector">
              <label>
                Course / Certification
                <select
                  required
                  value={bulkCourseId}
                  onChange={event => {
                    setBulkCourseId(event.target.value);
                    setError('');
                  }}
                  disabled={loadingAllocations}
                >
                  <option value="">
                    {loadingAllocations ? 'Loading allocated content…' : 'Select allocated Course & Certification'}
                  </option>
                  {allocations.map(a => (
                    <option key={a.allocation_id} value={a.course_id}>
                      {courseOptionLabel(a)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}

          <div className="bulk-upload-card">
            <div className="upload-icon">📄</div>
            <h3>Upload Registration File</h3>
            <p>Upload multiple participants for the selected course or certification.</p>

            <label htmlFor="bulk-upload" className="upload-box">
              <span className="upload-title">{bulkFile ? bulkFile.name : 'Choose CSV or Excel file'}</span>
              <span className="upload-subtitle">.csv, .xlsx or .xls</span>

              <input
                id="bulk-upload"
                type="file"
                accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                onChange={handleFileChange}
              />
            </label>

            <button type="button" className="template-btn" onClick={downloadTemplate}>
              Download {courseAssignMode === 'file' ? 'Per-Row-Course ' : ''}CSV Template
            </button>
          </div>

          <div className="csv-format-card">
            <h4>Required Columns</h4>

            <div className="csv-columns">
              <span>person_first_name</span>
              <span>person_last_name</span>
              <span>person_email</span>
              <span>person_mobile</span>
              <span>person_organisation</span>
              {courseAssignMode === 'file' && <span>course_code</span>}
            </div>

            <p>
              {courseAssignMode === 'file'
                ? 'Each row\'s course_code must match one of your allocated courses\' codes exactly (e.g. WZ-C-0001).'
                : 'Course ID is not required in the file. The selected course above will be applied to all participants.'}
            </p>
          </div>

          {bulkFile && processedRows.length > 0 && (
            <div className="csv-preview">
              <div className="preview-header">
                <div>
                  <h4>File Preview</h4>
                  <span>
                    {processedRows.length} records found
                    {hasRowErrors && ' · fix highlighted rows before uploading'}
                  </span>
                </div>

                <button type="button" className="remove-file" onClick={clearBulk}>
                  Remove
                </button>
              </div>

              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>First Name</th>
                      <th>Last Name</th>
                      <th>Email</th>
                      <th>Mobile</th>
                      <th>Organisation</th>
                      {courseAssignMode === 'file' && <th>Course Code</th>}
                      {courseAssignMode === 'file' && <th>Resolved Course</th>}
                    </tr>
                  </thead>

                  <tbody>
                    {processedRows.slice(0, 10).map(row => (
                      <tr key={row.id} style={row.rowError ? { background: 'rgba(220, 38, 38, 0.08)' } : undefined}>
                        <td>{row.id}</td>
                        <td>{row.person_first_name}</td>
                        <td>{row.person_last_name}</td>
                        <td>{row.person_email}</td>
                        <td>{row.person_mobile}</td>
                        <td>{row.person_organisation || '—'}</td>
                        {courseAssignMode === 'file' && <td>{row.course_code || '—'}</td>}
                        {courseAssignMode === 'file' && (
                          <td>
                            {row.rowError ? (
                              <span style={{ color: '#dc2626' }}>{row.rowError}</span>
                            ) : (
                              row.allocation?.course?.name || '—'
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {processedRows.length > 10 && (
                <div className="preview-more">Showing first 10 records of {processedRows.length}.</div>
              )}
            </div>
          )}

          {fileError && <div className="error">{fileError}</div>}
          {error && <div className="error">{error}</div>}

          <div className="form-actions">
            <button type="button" className="secondary" onClick={clearBulk}>
              Clear
            </button>

            <button
              type="submit"
              className="primary"
              disabled={
                submitting ||
                !bulkFile ||
                !processedRows.length ||
                hasRowErrors ||
                (courseAssignMode === 'same' && !bulkCourseId)
              }
            >
              {submitting ? 'Uploading...' : `Upload & Register${processedRows.length ? ` (${processedRows.length})` : ''}`}
            </button>
          </div>

          <div className="bulk-note">
            <b>Bulk Registration</b>
            <span>
              {courseAssignMode === 'file'
                ? 'Each participant is registered under the course their own course_code resolves to. Student IDs are generated immediately for every participant.'
                : 'All participants in this file will be registered under the selected course or certification. Student IDs are generated immediately for every participant.'}{' '}
              Organisation is optional per participant.
            </span>
          </div>
        </form>
      )}
    </div>
  );
}